// @ts-check
// Catálogo "Ações" do painel lateral (spec 2.0.0, fluxos.md seções 20.5 e 20.7; contrato 09 de fluxos, seção 17): busca,
// sanfonas por categoria (todas abertas no começo, estado guardado no navegador) e um cartão por ação, que se arrasta
// até um "+" do fluxo ou insere no ponto escolhido com clique, Enter ou Espaço. Ação que não cabe no ponto (divisão na
// profundidade 10, limite de passos) fica desabilitada com a explicação.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { normalizeSearchText } from '../../lib/utils.js';
import { FLOW_ACTION_CATEGORIES, FLOW_CATALOG_ITEMS } from '../../../../shared/flows/flow-catalog.js';
import { FLOW_ACTION_DRAG_TYPE } from './flow-connector.js';
import { categoryStyle } from './flow-ui.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../../../shared/flows/flow-catalog.js').FlowCatalogItem} FlowCatalogItem */

const CLOSED_STORAGE_KEY = 'appcrm.flowEditor.catalogClosed';
// ações novas da 2.0.0 que ganham o selo "Novo" no catálogo
const NEW_ITEMS = new Set(['SEND_WHATSAPP', 'WAIT_UNTIL_DATE', 'EMAIL_ENGAGEMENT']);

/** @returns {string[]} */
function readClosedCategories() {
  try {
    const raw = window.localStorage.getItem(CLOSED_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string[]} closed */
function storeClosedCategories(closed) {
  try {
    window.localStorage.setItem(CLOSED_STORAGE_KEY, JSON.stringify(closed));
  } catch {
    // sem armazenamento: as sanfonas voltam abertas na próxima vez
  }
}

/**
 * Itens que batem com a busca (nome da ação e da categoria, sem acento).
 * @param {string} search @returns {FlowCatalogItem[]}
 */
export function searchCatalogItems(search) {
  const needle = normalizeSearchText(search);
  if (!needle) return [...FLOW_CATALOG_ITEMS];
  return FLOW_CATALOG_ITEMS.filter((item) => normalizeSearchText(`${t(item.nameKey)} ${t(`flows.catalog.categories.${item.category}`)}`).includes(needle));
}

/**
 * @param {{ insertLabel?: string | null, disabledReason: (item: FlowCatalogItem) => string | null, onPick: (item: FlowCatalogItem, viaKeyboard: boolean) => void,
 *   onDragStart?: (item: FlowCatalogItem) => void, onDragEnd?: () => void, canDrag?: boolean }} props
 */
export function FlowActionCatalog({ insertLabel = null, disabledReason, onPick, onDragStart, onDragEnd, canDrag = true }) {
  const [search, setSearch] = useState('');
  const [closed, setClosed] = useState(readClosedCategories);
  const items = searchCatalogItems(search);
  /** @param {string} category */
  const toggle = (category) => {
    const next = closed.includes(category) ? closed.filter((value) => value !== category) : [...closed, category];
    setClosed(next);
    storeClosedCategories(next);
  };
  return html`<div class="flow-catalog" data-testid="flow-action-catalog">
    ${insertLabel ? html`<p class="flow-catalog-insert-point"><${Icon} name="IconPlus" size=${14} /><span>${insertLabel}</span></p>` : null}
    <p class="flow-catalog-help">${canDrag ? t('flows.catalog.help') : t('flows.catalog.helpTouch')}</p>
    <label class="flows-search flow-catalog-search">
      <${Icon} name="IconSearch" size=${16} />
      <input type="search" class="flows-search-input" value=${search} placeholder=${t('flows.catalog.search')} aria-label=${t('flows.catalog.search')}
        onInput=${(/** @type {InputEvent} */ event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
    </label>
    ${items.length === 0 ? html`<p class="flow-catalog-empty">${t('flows.catalog.noResults', { query: search })}</p>` : null}
    ${FLOW_ACTION_CATEGORIES.map((category) => {
      const categoryItems = items.filter((item) => item.category === category);
      if (categoryItems.length === 0) return null;
      // com busca, tudo aberto
      const isOpen = Boolean(search.trim()) || !closed.includes(category);
      const regionId = `flow-catalog-${category}`;
      return html`<section key=${category} class="flow-catalog-section">
        <h3 class="flow-catalog-heading">
          <button type="button" class="flow-catalog-toggle" aria-expanded=${isOpen ? 'true' : 'false'} aria-controls=${regionId} onClick=${() => toggle(category)}>
            <span>${t(`flows.catalog.categories.${category}`)}</span>
            <${Icon} name=${isOpen ? 'IconChevronUp' : 'IconChevronDown'} size=${14} />
          </button>
        </h3>
        ${isOpen
          ? html`<ul class="flow-catalog-items" id=${regionId} role="list">
              ${categoryItems.map((item) => {
                const reason = disabledReason(item);
                return html`<li key=${item.key}>
                  <div class="flow-catalog-item" role="button" tabindex=${reason ? -1 : 0} aria-disabled=${reason ? 'true' : undefined}
                    title=${reason ?? undefined} draggable=${canDrag && !reason ? 'true' : 'false'} data-testid=${`flow-catalog-${item.key}`}
                    aria-label=${reason ? `${t(item.nameKey)}. ${reason}` : t(item.nameKey)}
                    onClick=${(/** @type {MouseEvent} */ event) => !reason && onPick(item, event.detail === 0)}
                    onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
                      if ((event.key === 'Enter' || event.key === ' ') && !reason) {
                        event.preventDefault();
                        onPick(item, true);
                      }
                    }}
                    onDragStart=${(/** @type {DragEvent} */ event) => {
                      if (reason || !event.dataTransfer) return;
                      event.dataTransfer.effectAllowed = 'copy';
                      event.dataTransfer.setData(FLOW_ACTION_DRAG_TYPE, item.key);
                      event.dataTransfer.setData('text/plain', item.key);
                      onDragStart?.(item);
                    }}
                    onDragEnd=${() => onDragEnd?.()}>
                    <span class="flow-category-circle" data-size="24" data-category=${item.category} style=${categoryStyle(item.type)} aria-hidden="true"><${Icon} name=${item.icon} size=${14} /></span>
                    <span class="flow-catalog-item-name">${t(item.nameKey)}</span>
                    ${NEW_ITEMS.has(item.key) ? html`<span class="flow-catalog-badge">${t('flows.catalog.new')}</span>` : null}
                    ${canDrag ? html`<span class="flow-catalog-grip" aria-hidden="true"><${Icon} name="IconGripVertical" size=${16} /></span>` : null}
                  </div>
                </li>`;
              })}
            </ul>`
          : null}
      </section>`;
    })}
  </div>`;
}
